import { PDFDocument } from "pdf-lib";

/**
 * Merges the given invoice PDFs into a single document and opens the browser
 * print dialog once for the whole batch.
 *
 * Invoices that cannot be fetched or parsed are skipped so one broken file
 * never blocks the rest of the batch.
 *
 * @param {string[]} urls absolute urls of the invoice pdfs
 * @returns {Promise<{printed: number, failed: number}>}
 */
export const printInvoices = async (urls = []) => {
  const uniqueUrls = [...new Set(urls.filter(Boolean))];

  if (uniqueUrls.length === 0) {
    return { printed: 0, failed: 0 };
  }

  const merged = await PDFDocument.create();
  let failed = 0;

  for (const url of uniqueUrls) {
    console.log({ url });

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }
      const bytes = await response.arrayBuffer();
      const pdf = await PDFDocument.load(bytes, { ignoreEncryption: true });
      const pages = await merged.copyPages(pdf, pdf.getPageIndices());
      pages.forEach((page) => merged.addPage(page));
    } catch (error) {
      failed += 1;
      console.error("Invoice skipped:", url, error);
    }
  }

  const printed = uniqueUrls.length - failed;
  if (printed === 0) {
    return { printed, failed };
  }

  const blob = new Blob([await merged.save()], { type: "application/pdf" });
  const blobUrl = window.URL.createObjectURL(blob);

  // A hidden iframe keeps the print dialog inside the current tab, so pop-up
  // blockers never get in the way.
  const frame = document.createElement("iframe");
  frame.style.position = "fixed";
  frame.style.right = "0";
  frame.style.bottom = "0";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  frame.src = blobUrl;

  frame.onload = () => {
    try {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    } catch (error) {
      // Some browsers refuse to script the embedded pdf viewer - fall back to
      // opening the merged file in a new tab so the user can print manually.
      console.error("Print error:", error);
      window.open(blobUrl, "_blank");
    }

    // Keep the frame alive long enough for the dialog to read the document.
    setTimeout(() => {
      frame.remove();
      window.URL.revokeObjectURL(blobUrl);
    }, 60000);
  };

  document.body.appendChild(frame);

  return { printed, failed };
};
